import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { getLevel } from '@/utils/readingLevels';

const SharedStoryViewer = () => {
  const { shareKey } = useParams();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSharedStory();
  }, [shareKey]);

  const fetchSharedStory = async () => {
    try {
      const response = await axios.get(`${API}/shared/${shareKey}`);
      setStory(response.data);
    } catch (error) {
      console.error('Failed to fetch shared story:', error);
      setError('Story not found or no longer shared');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center playful-gradient">
        <div className="text-center">
          <BookOpen className="w-16 h-16 mx-auto mb-4 animate-pulse" style={{ color: 'var(--accent-2)' }} />
          <p className="text-2xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
            Loading story...
          </p>
        </div>
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className="min-h-screen flex items-center justify-center playful-gradient">
        <div className="story-card text-center py-12 max-w-md">
          <BookOpen className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-secondary)' }} />
          <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
            Story Not Found
          </h1>
          <p className="text-lg mb-8" style={{ color: 'var(--text-secondary)' }}>
            {error || 'This story may have been removed or is no longer shared.'}
          </p>
          <Button
            onClick={() => window.location.href = '/'}
            className="pill-button"
            style={{ background: 'var(--accent-2)', color: 'white' }}
          >
            Go to StoryTime Studio
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen playful-gradient page-enter">
      <div className="container mx-auto px-6 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <BookOpen className="w-12 h-12" style={{ color: 'var(--accent-2)' }} strokeWidth={2.5} />
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
              {story.title}
            </h1>
          </div>
          <p className="text-sm capitalize" style={{ color: 'var(--text-secondary)' }}>
            Shared from StoryTime Studio • Reading Level: {getLevel(story.reading_level)}
          </p>
        </div>

        {/* Story Content */}
        <div className="story-card p-4 sm:p-6">
          {story.images && story.images.length > 0 && (
            <div className="mb-6 flex justify-center">
              <img
                src={`data:image/png;base64,${story.images[0]}`}
                alt="Story illustration"
                className="max-w-full h-auto rounded-2xl"
                style={{ maxHeight: '400px' }}
              />
            </div>
          )}
          
          <div className="prose max-w-none mb-8">
            <p className="text-lg leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--text-primary)' }}>
              {story.content}
            </p>
          </div>
          
          {story.moral && (
            <div className="mt-8 p-6 rounded-2xl" style={{ background: 'var(--accent-5)', color: 'white' }}>
              <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                The Moral
              </h3>
              <p className="text-lg">{story.moral}</p>
            </div>
          )}
        </div>

        {/* Call to Action */}
        <div className="mt-12 text-center story-card p-8">
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
            You can also create your own stories!
          </h2>
          <p className="text-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
            Generate personalized bedtime stories for your children with AI-powered illustrations and custom characters.
          </p>
          <Button
            onClick={() => window.location.href = '/'}
            className="pill-button"
            style={{ background: 'var(--accent-2)', color: 'white' }}
          >
            <BookOpen className="mr-2 w-5 h-5" />
            Try StoryTime Studio
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SharedStoryViewer;
